/**
 * Cart Progress Bar v5.7 - COMPLETE TRUCK JUMP FIX
 * - Truck NEVER jumps to 0% - stores and restores last position
 * - Waits for real cart data before positioning
 * - Survives DOM re-renders without reset
 * - Green milestone dots, blue progress bar
 */

class CartProgressBar {
  constructor() {
    // Configuration
    this.MILESTONES = [
      { value: 35, title: 'Free Shipping', icon: '🚚', description: 'Free Shipping' },
      { value: 99, title: '10% Off', icon: '💰', description: '10% Off Your Order' },
      { value: 249, title: 'Free Gift', icon: '🎁', description: 'Free Gift' }
    ];
    
    this.MAX_VALUE = this.MILESTONES[this.MILESTONES.length - 1].value;
    this.CART_EXPIRY_MINUTES = 30;
    
    // State
    this.cartTotal = 0;
    this.previousTotal = 0;
    this.progress = 0;
    this.currentMilestoneIndex = -1;
    this.elements = {};
    
    // CRITICAL: Store last truck position to survive DOM re-renders
    this.lastProgress = null;
    
    // Update control
    this.isFetching = false;
    this.isUpdatingVisuals = false;
    this.lastUpdateTime = 0;
    this.updateDebounceMs = 300;
    this.pendingUpdateTimer = null;
    
    // Final milestone tracking
    this.finalMilestoneReached = false;
    
    // Timer state
    this.timerInterval = null;
    this.expiryTime = null;
    
    this.init();
  }

  init() {
    console.log('🚀 Initializing Cart Progress Bar v5.7...');
    
    this.findElements();
    this.loadSavedState();
    
    // Calculate state from saved data (if any)
    this.calculateMilestoneState();
    
    // Update message
    this.updateMessage();
    
    // FIX #1: DO NOT set truck position before cart data is loaded
    // Wait for real cart data first
    this.fetchCartAndUpdate(true);
    
    // Setup
    this.setupEventListeners();
    this.startMonitoring();
    this.initTimer();
    
    console.log('✅ Cart Progress Bar initialized');
  }

  findElements() {
    this.container = document.querySelector('[data-cart-progress]');
    
    if (!this.container) {
      console.warn('⚠️ Progress bar container not found');
      return;
    }
    
    this.elements = {
      fill: this.container.querySelector('[data-progress-fill]'),
      truck: this.container.querySelector('[data-progress-truck]'),
      message: this.container.querySelector('[data-progress-message]'),
      messageDefault: this.container.querySelector('[data-message-default]'),
      messageUnlocked: this.container.querySelector('[data-message-unlocked]'),
      messageComplete: this.container.querySelector('[data-message-complete]'),
      amountNeeded: this.container.querySelector('[data-amount-needed]'),
      nextPromoTitle: this.container.querySelector('[data-next-promo-title]'),
      unlockedPromoTitle: this.container.querySelector('[data-unlocked-promo-title]'),
      nextAfterUnlock: this.container.querySelector('[data-next-after-unlock]'),
      confettiContainer: this.container.querySelector('[data-confetti-container]'),
      milestones: this.container.querySelectorAll('[data-milestone]'),
      timer: this.container.querySelector('[data-cart-timer]'),
      timerText: this.container.querySelector('[data-timer-text]')
    };
  }

  loadSavedState() {
    try {
      const savedTotal = sessionStorage.getItem('cart_progress_total');
      const savedExpiry = sessionStorage.getItem('cart_expiry_time');
      const savedFinalReached = sessionStorage.getItem('cart_final_milestone_reached');
      const savedProgress = sessionStorage.getItem('cart_progress_position');
      
      if (savedTotal) {
        this.cartTotal = parseFloat(savedTotal);
        this.previousTotal = this.cartTotal;
      }
      
      if (savedExpiry) {
        this.expiryTime = parseInt(savedExpiry);
      }

      if (savedFinalReached === 'true') {
        this.finalMilestoneReached = true;
      }
      
      // Restore last truck position
      if (savedProgress) {
        this.lastProgress = parseFloat(savedProgress);
        console.log(`💾 Restored last position: ${this.lastProgress.toFixed(1)}%`);
      }
      
      if (this.cartTotal > 0) {
        console.log(`💾 Loaded state: QAR ${this.cartTotal.toFixed(2)}`);
      }
    } catch (e) {
      console.warn('⚠️ Could not load saved state:', e);
    }
  }

  saveState() {
    try {
      sessionStorage.setItem('cart_progress_total', this.cartTotal.toString());
      if (this.expiryTime) {
        sessionStorage.setItem('cart_expiry_time', this.expiryTime.toString());
      }
      sessionStorage.setItem('cart_final_milestone_reached', this.finalMilestoneReached.toString());
      
      // Save last truck position
      if (this.lastProgress !== null) {
        sessionStorage.setItem('cart_progress_position', this.lastProgress.toString());
      }
    } catch (e) {
      console.warn('⚠️ Could not save state:', e);
    }
  }

  clearState() {
    try {
      sessionStorage.removeItem('cart_progress_total');
      sessionStorage.removeItem('cart_expiry_time');
      sessionStorage.removeItem('cart_final_milestone_reached');
      sessionStorage.removeItem('cart_progress_position');
      console.log('🗑️ Cleared progress state');
    } catch (e) {
      console.warn('⚠️ Could not clear state:', e);
    }
  }

  // ========== INITIAL POSITION SETUP ==========
  
  setInitialTruckPosition() {
    if (!this.elements.truck || !this.elements.fill) return;
    
    console.log(`🎯 Setting initial position: ${this.progress.toFixed(1)}%`);
    
    // Disable transitions temporarily
    this.elements.truck.style.transition = 'none';
    this.elements.fill.style.transition = 'none';
    
    // Set initial positions
    this.elements.truck.style.left = `${this.progress}%`;
    this.elements.fill.style.width = `${this.progress}%`;
    
    // Force browser to apply the styles
    void this.elements.truck.offsetWidth;
    void this.elements.fill.offsetWidth;
    
    // Re-enable transitions IMMEDIATELY (critical for smooth future updates)
    this.elements.truck.style.transition = 'left 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.8s ease, visibility 0.8s ease';
    this.elements.fill.style.transition = 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)';
    
    // Mark that initial position is set
    this.hasSetInitialPosition = true;
    
    // Update milestone markers
    this.updateMilestoneMarkers();
  }

  // ========== MILESTONE CALCULATION ==========
  
  calculateMilestoneState() {
    // Find the highest milestone reached
    this.currentMilestoneIndex = -1;
    
    for (let i = 0; i < this.MILESTONES.length; i++) {
      if (this.cartTotal >= this.MILESTONES[i].value) {
        this.currentMilestoneIndex = i;
      } else {
        break;
      }
    }
    
    // Calculate progress percentage
    this.progress = Math.min((this.cartTotal / this.MAX_VALUE) * 100, 100);

    // Check if final milestone reached
    const isFinalMilestone = this.currentMilestoneIndex === this.MILESTONES.length - 1;
    if (isFinalMilestone && !this.finalMilestoneReached) {
      this.finalMilestoneReached = true;
      console.log('🎁 FINAL MILESTONE REACHED!');
    }
  }

  getNextMilestone() {
    return this.MILESTONES.find(m => this.cartTotal < m.value) || null;
  }

  getCurrentMilestone() {
    if (this.currentMilestoneIndex >= 0) {
      return this.MILESTONES[this.currentMilestoneIndex];
    }
    return null;
  }

  // ========== TIMER FUNCTIONALITY ==========
  
  initTimer() {
    if (this.cartTotal > 0) {
      this.startTimer();
    }
  }

  startTimer() {
    if (!this.expiryTime) {
      this.expiryTime = Date.now() + (this.CART_EXPIRY_MINUTES * 60 * 1000);
      this.saveState();
    }
    
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    
    this.updateTimerDisplay();
    this.timerInterval = setInterval(() => this.updateTimerDisplay(), 1000);
  }

  stopTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
    
    if (this.elements.timer) {
      this.elements.timer.style.display = 'none';
    }
  }

  updateTimerDisplay() {
    if (!this.elements.timer || !this.elements.timerText || !this.expiryTime) return;
    
    const now = Date.now();
    const remaining = Math.max(0, this.expiryTime - now);
    
    if (remaining === 0) {
      this.elements.timerText.textContent = 'Cart expired';
      this.stopTimer();
      return;
    }
    
    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);
    
    this.elements.timerText.textContent = `${minutes}:${seconds.toString().padStart(2, '0')}`;
    this.elements.timer.style.display = 'flex';
  }

  // ========== EVENT LISTENERS ==========
  
  setupEventListeners() {
    document.addEventListener('cart:updated', (e) => {
      console.log('📢 cart:updated received');
      this.handleCartEvent(e);
    });

    document.addEventListener('cart:change', (e) => {
      console.log('📢 cart:change received');
      this.handleCartEvent(e);
    });

    document.addEventListener('shopify:section:load', () => {
      setTimeout(() => {
        this.findElements();
        this.calculateMilestoneState();
        
        // FIX #3: Always restore last known position after DOM re-render
        if (this.lastProgress !== null) {
          console.log(`🔄 Restoring position after section reload: ${this.lastProgress.toFixed(1)}%`);
          
          // Disable transitions temporarily
          this.elements.truck.style.transition = 'none';
          this.elements.fill.style.transition = 'none';
          
          // Restore last position
          this.elements.truck.style.left = `${this.lastProgress}%`;
          this.elements.fill.style.width = `${this.lastProgress}%`;
          
          // Force reflow
          void this.elements.truck.offsetWidth;
          
          // Re-enable transitions
          this.elements.truck.style.transition = 'left 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.8s ease, visibility 0.8s ease';
          this.elements.fill.style.transition = 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)';
        }
        
        this.updateMessage();
        this.scheduleUpdate('section-load');
      }, 50);
    });

    this.observeCartDrawer();

    document.addEventListener('change', (e) => {
      if (e.target.matches('input[name="updates[]"], input[name*="quantity"], .quantity__input')) {
        console.log('🔢 Quantity changed');
        this.scheduleUpdate('quantity-change');
      }
    });

    document.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('[data-cart-remove], .cart-remove-button, button[name="update"]');
      if (removeBtn) {
        console.log('🗑️ Remove clicked');
        setTimeout(() => this.scheduleUpdate('item-remove'), 150);
      }
    });

    this.interceptCartRequests();
  }

  handleCartEvent(e) {
    if (this.pendingUpdateTimer) {
      clearTimeout(this.pendingUpdateTimer);
      this.pendingUpdateTimer = null;
    }

    if (e.detail && e.detail.cart) {
      const newTotal = e.detail.cart.total_price / 100;
      console.log('📦 Using cart data from event: QAR', newTotal.toFixed(2));
      this.processCartUpdate(newTotal);
    } else {
      this.scheduleUpdate('event-fallback');
    }
  }

  scheduleUpdate(source = 'unknown') {
    const now = Date.now();
    const timeSinceLastUpdate = now - this.lastUpdateTime;

    if (this.pendingUpdateTimer) {
      clearTimeout(this.pendingUpdateTimer);
      this.pendingUpdateTimer = null;
    }

    if (timeSinceLastUpdate < this.updateDebounceMs) {
      console.log(`⏱️ Debouncing update from ${source}`);
      this.pendingUpdateTimer = setTimeout(() => {
        this.pendingUpdateTimer = null;
        this.fetchCartAndUpdate(false);
      }, this.updateDebounceMs - timeSinceLastUpdate);
      return;
    }

    this.fetchCartAndUpdate(false);
  }

  observeCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer-component, [data-cart-drawer]');
    if (!cartDrawer) return;

    const dialog = cartDrawer.querySelector('dialog, [role="dialog"]');
    if (!dialog) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'open') {
          if (dialog.hasAttribute('open') || dialog.open) {
            setTimeout(() => {
              this.findElements();
              this.fetchCartAndUpdate(true);
              
              if (this.cartTotal > 0 && !this.timerInterval) {
                this.startTimer();
              }
            }, 20);
          }
        }
      });
    });

    observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });
  }

  interceptCartRequests() {
    const originalFetch = window.fetch;
    const self = this;

    window.fetch = async function (...args) {
      const response = await originalFetch(...args);
      const url = args[0];

      if (typeof url === 'string' &&
        (url.includes('/cart/add') ||
          url.includes('/cart/change') ||
          url.includes('/cart/update') ||
          url.includes('/cart/clear'))) {
        console.log('🌐 Cart API call detected');
      }

      return response;
    };
  }

  startMonitoring() {
    setInterval(() => {
      const cartDrawer = document.querySelector('cart-drawer-component dialog[open], [data-cart-drawer][open]');
      if (cartDrawer && !this.isFetching && !this.isUpdatingVisuals) {
        const timeSinceLastUpdate = Date.now() - this.lastUpdateTime;
        if (timeSinceLastUpdate > 5000) {
          this.fetchCartAndUpdate(true);
        }
      }
    }, 5000);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.querySelector && node.querySelector('[data-cart-progress]')) {
            setTimeout(() => {
              this.findElements();
              this.calculateMilestoneState();
              
              // Restore last position after DOM mutation
              if (this.lastProgress !== null && this.elements.truck) {
                this.elements.truck.style.transition = 'none';
                this.elements.fill.style.transition = 'none';
                
                this.elements.truck.style.left = `${this.lastProgress}%`;
                this.elements.fill.style.width = `${this.lastProgress}%`;
                
                void this.elements.truck.offsetWidth;
                
                this.elements.truck.style.transition = 'left 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.8s ease, visibility 0.8s ease';
                this.elements.fill.style.transition = 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)';
              }
              
              this.updateMessage();
            }, 20);
          }
        });
      });
    });

    const target = document.querySelector('cart-drawer-component, body');
    if (target) {
      observer.observe(target, { childList: true, subtree: true });
    }
  }

  // ========== CART UPDATE ==========
  
  async fetchCartAndUpdate(silent = false) {
    if (this.isFetching) {
      console.log('⏳ Already fetching, skipping...');
      return;
    }

    this.isFetching = true;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const newTotal = cart.total_price / 100;
      const isEmpty = cart.item_count === 0 || newTotal === 0;

      if (!silent) {
        console.log('✅ Cart fetched:', { items: cart.item_count, total: `QAR ${newTotal.toFixed(2)}` });
      }

      if (isEmpty) {
        if (this.cartTotal > 0) {
          this.handleEmptyCart();
        }
        return;
      }

      if (Math.abs(newTotal - this.cartTotal) >= 0.01) {
        this.processCartUpdate(newTotal);
      } else if (this.lastProgress === null && newTotal > 0) {
        // First load with existing cart - set position without animation
        this.cartTotal = newTotal;
        this.calculateMilestoneState();
        this.setInitialTruckPosition();
        this.updateMessage();
        this.saveState();
        
        if (!this.timerInterval) {
          this.startTimer();
        }
      } else {
        if (!silent) {
          console.log('⏭️ No change detected');
        }
      }

    } catch (error) {
      console.error('❌ Error fetching cart:', error);
    } finally {
      this.isFetching = false;
    }
  }

  processCartUpdate(newTotal) {
    if (this.isUpdatingVisuals) {
      console.log('⏳ Visual update in progress, skipping duplicate');
      return;
    }

    this.lastUpdateTime = Date.now();
    this.isUpdatingVisuals = true;

    const oldTotal = this.cartTotal;
    const oldMilestoneIndex = this.currentMilestoneIndex;
    
    this.previousTotal = this.cartTotal;
    this.cartTotal = newTotal;
    
    console.log(`🔄 Processing: QAR ${oldTotal.toFixed(2)} → QAR ${newTotal.toFixed(2)}`);
    
    // Recalculate milestone state
    this.calculateMilestoneState();
    
    // Check milestone crossing - trigger confetti for ANY milestone crossed
    if (this.currentMilestoneIndex > oldMilestoneIndex) {
      console.log('🎊 Milestone crossed!');
      this.triggerConfetti();
    }
    
    // Update message
    this.updateMessage();
    
    // Update truck position smoothly (NEVER resets to 0%)
    this.updateTruckPosition();
    
    this.saveState();
    
    if (newTotal > 0 && !this.timerInterval) {
      this.startTimer();
    }

    setTimeout(() => {
      this.isUpdatingVisuals = false;
      console.log('✅ Update complete');
    }, 500);
  }

  handleEmptyCart() {
    console.log('🔄 Cart emptied');

    this.cartTotal = 0;
    this.previousTotal = 0;
    this.progress = 0;
    this.currentMilestoneIndex = -1;
    this.finalMilestoneReached = false;

    this.clearState();
    
    // Update message
    this.updateMessage();
    
    // Stop timer
    this.stopTimer();
    this.expiryTime = null;
    
    // Show truck again and smoothly move to start
    if (this.elements.truck) {
      this.elements.truck.style.opacity = '1';
      this.elements.truck.style.visibility = 'visible';
      this.elements.truck.style.left = '0%';
    }
    
    // Smoothly reset progress fill
    if (this.elements.fill) {
      this.elements.fill.style.width = '0%';
    }
    
    // Update milestone markers
    this.updateMilestoneMarkers();
    
    this.isUpdatingVisuals = false;
    this.lastUpdateTime = Date.now();
  }

  // ========== VISUAL UPDATES - TRUCK NEVER JUMPS ==========
  
  updateTruckPosition() {
    if (!this.elements.truck || !this.elements.fill) return;
    
    const currentLeft = this.elements.truck.style.left || '0%';
    const currentPosition = parseFloat(currentLeft);
    
    console.log(`🚛 Truck moving smoothly: ${currentPosition.toFixed(1)}% → ${this.progress.toFixed(1)}%`);
    
    // CRITICAL: Just update the position - CSS transition handles the smooth animation
    // NEVER disable or reset transition property after initial setup
    this.elements.truck.style.left = `${this.progress}%`;
    this.elements.fill.style.width = `${this.progress}%`;
    
    // Handle visibility for final milestone
    if (this.finalMilestoneReached) {
      setTimeout(() => {
        if (this.elements.truck) {
          this.elements.truck.style.opacity = '0';
          setTimeout(() => {
            if (this.elements.truck) {
              this.elements.truck.style.visibility = 'hidden';
            }
          }, 800);
        }
      }, 400);
    } else {
      this.elements.truck.style.opacity = '1';
      this.elements.truck.style.visibility = 'visible';
    }
    
    // Update milestone markers
    this.updateMilestoneMarkers();
  }

  updateMilestoneMarkers() {
    if (!this.elements.milestones) return;

    this.elements.milestones.forEach((el) => {
      const value = parseFloat(el.dataset.milestone);
      const isUnlocked = this.cartTotal >= value;
      const milestone = this.MILESTONES.find(m => m.value === value);

      if (isUnlocked) {
        el.classList.add('unlocked');
        const icon = el.querySelector('.milestone-icon');
        if (icon && milestone) {
          icon.textContent = milestone.icon;
        }
      } else {
        el.classList.remove('unlocked');
        const icon = el.querySelector('.milestone-icon');
        if (icon) {
          icon.textContent = '🔒';
        }
      }
    });
  }

  // ========== MESSAGE UPDATE ==========
  
  updateMessage() {
    if (!this.elements.message) return;

    const nextMilestone = this.getNextMilestone();
    const currentMilestone = this.getCurrentMilestone();

    // All milestones completed
    if (!nextMilestone) {
      this.showMessage('complete');
      return;
    }

    // No milestones unlocked yet
    if (!currentMilestone) {
      const amountNeeded = Math.ceil(nextMilestone.value - this.cartTotal);
      this.showMessage('default', {
        amount: amountNeeded,
        title: nextMilestone.description
      });
      return;
    }

    // At least one milestone unlocked
    const amountNeeded = Math.ceil(nextMilestone.value - this.cartTotal);
    this.showMessage('unlocked', {
      unlockedTitle: currentMilestone.description,
      nextAmount: amountNeeded,
      nextTitle: nextMilestone.description
    });
  }

  showMessage(type, data = {}) {
    // Hide all messages
    if (this.elements.messageDefault) this.elements.messageDefault.style.display = 'none';
    if (this.elements.messageUnlocked) this.elements.messageUnlocked.style.display = 'none';
    if (this.elements.messageComplete) this.elements.messageComplete.style.display = 'none';

    // Show correct message
    if (type === 'complete') {
      if (this.elements.messageComplete) {
        this.elements.messageComplete.style.display = 'block';
      }
    } else if (type === 'unlocked') {
      if (this.elements.messageUnlocked) {
        this.elements.messageUnlocked.style.display = 'block';

        if (this.elements.unlockedPromoTitle) {
          // Wrap "Your Order" or similar secondary text in span for font size control
          const promoText = data.unlockedTitle;
          const formattedText = this.formatUnlockedPromoText(promoText);
          this.elements.unlockedPromoTitle.innerHTML = formattedText;
        }

        if (this.elements.nextAfterUnlock) {
          this.elements.nextAfterUnlock.innerHTML = `Add <strong>QAR ${data.nextAmount}</strong> more to unlock <strong>${data.nextTitle}</strong>`;
        }
      }
    } else {
      if (this.elements.messageDefault) {
        this.elements.messageDefault.style.display = 'block';

        if (this.elements.amountNeeded) {
          this.elements.amountNeeded.textContent = `QAR ${data.amount}`;
        }

        if (this.elements.nextPromoTitle) {
          this.elements.nextPromoTitle.textContent = data.title;
        }
      }
    }
  }
  
  formatUnlockedPromoText(text) {
    // Wrap secondary phrases in smaller span for better fit
    const patterns = [
      { regex: /Your Order/i, replacement: '<span class="secondary-text">Your Order</span>' },
      { regex: /Your Purchase/i, replacement: '<span class="secondary-text">Your Purchase</span>' },
      { regex: /On Orders/i, replacement: '<span class="secondary-text">On Orders</span>' }
    ];
    
    let formattedText = text;
    for (const pattern of patterns) {
      if (pattern.regex.test(formattedText)) {
        formattedText = formattedText.replace(pattern.regex, pattern.replacement);
        break; // Only apply one pattern
      }
    }
    
    return formattedText;
  }

  // ========== CONFETTI EFFECTS ==========

  triggerConfetti() {
    if (!this.elements.confettiContainer) return;

    const colors = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
    const count = 40;

    for (let i = 0; i < count; i++) {
      const piece = document.createElement('div');
      piece.className = 'confetti-piece';
      piece.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];

      const angle = (Math.random() * 360) * (Math.PI / 180);
      const distance = 60 + Math.random() * 120;
      const tx = Math.cos(angle) * distance;
      const ty = Math.sin(angle) * distance;
      const rotate = Math.random() * 720 - 360;

      piece.style.setProperty('--tx', `${tx}px`);
      piece.style.setProperty('--ty', `${ty}px`);
      piece.style.setProperty('--rotate', `${rotate}deg`);
      piece.style.animationDelay = `${Math.random() * 0.3}s`;

      this.elements.confettiContainer.appendChild(piece);
      setTimeout(() => piece.remove(), 1500);
    }
  }

  forceUpdate() {
    this.scheduleUpdate('manual-force');
  }

  reset() {
    this.handleEmptyCart();
  }
}

// Initialize
let progressBarInstance = null;

const initProgressBar = () => {
  if (!progressBarInstance) {
    progressBarInstance = new CartProgressBar();
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProgressBar);
} else {
  initProgressBar();
}

window.CartProgressBar = CartProgressBar;
window.cartProgressBar = progressBarInstance;